import { Search, Filter, X } from 'lucide-react'
import { useState } from 'react'

type StatusFilter = 'all' | 'active' | 'trial' | 'suspended' | 'past_due' | 'canceled' | 'churned' | 'none'

interface OrganizationFiltersState {
  search?: string
  status?: StatusFilter
  engagement_min?: number
  engagement_max?: number
  trial_expiring_days?: number
}

interface OrganizationFiltersProps {
  onFiltersChange: (filters: OrganizationFiltersState) => void
  initialFilters?: OrganizationFiltersState
}

export function OrganizationFilters({ onFiltersChange, initialFilters = {} }: OrganizationFiltersProps) {
  const [search, setSearch] = useState(initialFilters.search || '')
  const [status, setStatus] = useState(initialFilters.status || 'all')
  const [engagementMin, setEngagementMin] = useState<string>(initialFilters.engagement_min?.toString() || '')
  const [engagementMax, setEngagementMax] = useState<string>(initialFilters.engagement_max?.toString() || '')
  const [trialExpiringDays, setTrialExpiringDays] = useState<string>(
    initialFilters.trial_expiring_days?.toString() || ''
  )
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    applyFilters({ search: value || undefined })
  }

  const handleStatusChange = (value: StatusFilter) => {
    setStatus(value)
    applyFilters({ status: value === 'all' ? undefined : value })
  }

  const applyFilters = (overrides: Partial<OrganizationFiltersState> = {}) => {
    onFiltersChange({
      search: search || undefined,
      status: status === 'all' ? undefined : status,
      engagement_min: engagementMin ? parseInt(engagementMin) : undefined,
      engagement_max: engagementMax ? parseInt(engagementMax) : undefined,
      trial_expiring_days: trialExpiringDays ? parseInt(trialExpiringDays) : undefined,
      ...overrides,
    })
  }

  const clearFilters = () => {
    setSearch('')
    setStatus('all')
    setEngagementMin('')
    setEngagementMax('')
    setTrialExpiringDays('')
    onFiltersChange({})
  }

  const hasActiveFilters = search || status !== 'all' || engagementMin || engagementMax || trialExpiringDays

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'trial', label: 'Em Trial' },
    { value: 'active', label: 'Ativo' },
    { value: 'past_due', label: 'Pagamento Atrasado' },
    { value: 'suspended', label: 'Suspenso' },
    { value: 'canceled', label: 'Cancelado' },
  ]

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[minmax(260px,1fr)_auto_auto_auto] md:items-center">
        {/* Search */}
        <div className="relative min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome, slug ou email..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent md:w-auto"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex w-full items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors md:w-auto ${
            showAdvanced ? 'border-teal-500 text-teal-600 bg-teal-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filtros Avançados
        </button>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex w-full items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors md:w-auto"
          >
            <X className="w-4 h-4" />
            Limpar Filtros
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Engagement Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Engajamento</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Mín"
                  min="0"
                  max="100"
                  value={engagementMin}
                  onChange={(e) => setEngagementMin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Máx"
                  min="0"
                  max="100"
                  value={engagementMax}
                  onChange={(e) => setEngagementMax(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Trial Expiring */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Trial Expirando em (dias)</label>
              <input
                type="number"
                placeholder="Ex: 7"
                min="1"
                max="30"
                value={trialExpiringDays}
                onChange={(e) => setTrialExpiringDays(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={() => applyFilters()}
                className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
